import {Grid, makeStyles} from '@material-ui/core';
import {useQuoteField} from 'Quoting/components/hooks/useHandleQuoteFieldChange';
import {useQuoteFormValidation} from 'Quoting/components/hooks/useQuoteFormValidation';
import {FormRowGrid, GridRowProps} from 'Quoting/components/shared/GridRow';
import {QuoteFormControlElement} from 'Quoting/components/shared/QuoteFormControlElement';
import {QuoteFormElement} from 'Quoting/components/shared/QuoteFormElement';
import {FormWrapper} from 'Quoting/components/shared/QuoteFormWrapper';
import {A360ProductCd} from 'Quoting/models/Quoting';
import {isDefined} from 'Quoting/utils/utilities';
import React from 'react';
import {useSelector} from 'react-redux';
import {ReducerState} from 'rootReducer';
import {getBenefitArrayPaths} from '../Common/PropertyPaths';
import {getFieldError, getValidationRuleset} from '../rules/rulesValidator';

const coverageIndex = 0;

const benefitName = 'coveredConditionsDetails';

const usePortabilityStyles = makeStyles(() => ({
  columnAlignment: {
    alignItems: 'start',
  },
}));

const InitialDiagnosisBlock = () => {
  const {getQuoteFieldValue, updateQuoteFieldValue} = useQuoteField();
  const handleValidationOnBlur = useQuoteFormValidation();

  const {selectedPlan} = useSelector((state: ReducerState) => state.quoteForm);

  const initialPath = getBenefitArrayPaths(selectedPlan?.planIndex, coverageIndex, benefitName, 0);

  const ConditionPercentageError = (attributeName: string, amount: number, path: string) =>
    getFieldError(
      `CICoveredConditions.${attributeName}`,
      getQuoteFieldValue<number>(path) * 100,
      getValidationRuleset(A360ProductCd.CI, {attributeName, amount})
    );

  const initialDiagnosis: GridRowProps[] = [
    {
      rowProps: {key: 'illness-conditions-row-1'},
      title: 'Critical Illness Conditions',
      items: [
        {
          key: 'illness-conditions-heart-attack',
          element: (
            <QuoteFormElement
              elementType="Text"
              disabled
              label="Heart Attack (100%)"
              adornment="percentage"
              type="number"
              value={getQuoteFieldValue<number>(initialPath.heartAttackPct)}
              id={initialPath.heartAttackPct}
              onChange={e => updateQuoteFieldValue(initialPath.heartAttackPct, Number(e.target.value) / 100)}
              onBlur={handleValidationOnBlur}
              error={isDefined(ConditionPercentageError('heartAttackPct', 200, initialPath.heartAttackPct))}
              helperText={ConditionPercentageError('heartAttackPct', 200, initialPath.heartAttackPct)}
              required
            />
          ),
          props: {xs: 12, sm: 12, md: 12},
        },
        {
          key: 'illness-conditions-stroke',
          element: (
            <QuoteFormElement
              elementType="Text"
              disabled
              label="Stroke (100%)"
              adornment="percentage"
              type="number"
              value={getQuoteFieldValue<number>(initialPath.strokePct)}
              id={initialPath.strokePct}
              onChange={e => updateQuoteFieldValue(initialPath.strokePct, Number(e.target.value) / 100)}
              onBlur={() => {
                handleValidationOnBlur();
              }}
              error={isDefined(ConditionPercentageError('strokePct', 200, initialPath.strokePct))}
              helperText={ConditionPercentageError('strokePct', 200, initialPath.strokePct)}
              required
            />
          ),
          props: {xs: 12, sm: 12, md: 12},
        },
        {
          key: 'illness-conditions-renal-failure',
          element: (
            <QuoteFormElement
              elementType="Text"
              disabled
              label="End Stage Renal Failure (100%)"
              adornment="percentage"
              type="number"
              value={getQuoteFieldValue<number>(initialPath.kidneyRenalFailurePct)}
              id={initialPath.kidneyRenalFailurePct}
              onChange={e => updateQuoteFieldValue(initialPath.kidneyRenalFailurePct, Number(e.target.value) / 100)}
              onBlur={() => {
                handleValidationOnBlur();
              }}
              error={isDefined(
                ConditionPercentageError('kidneyRenalFailurePct', 200, initialPath.kidneyRenalFailurePct)
              )}
              helperText={ConditionPercentageError('kidneyRenalFailurePct', 200, initialPath.kidneyRenalFailurePct)}
              required
            />
          ),
          props: {xs: 12, sm: 12, md: 12},
        },
        {
          key: 'illness-conditions-organ-failure',
          element: (
            <QuoteFormElement
              elementType="Text"
              disabled
              label="Major Organ Transplant (100%)"
              adornment="percentage"
              type="number"
              value={getQuoteFieldValue<number>(initialPath.majorOrganFailurePct)}
              id={initialPath.majorOrganFailurePct}
              onChange={e => updateQuoteFieldValue(initialPath.majorOrganFailurePct, Number(e.target.value) / 100)}
              onBlur={() => {
                handleValidationOnBlur();
              }}
              error={isDefined(ConditionPercentageError('majorOrganFailurePct', 200, initialPath.majorOrganFailurePct))}
              helperText={ConditionPercentageError('majorOrganFailurePct', 200, initialPath.majorOrganFailurePct)}
              required
            />
          ),
          props: {xs: 12, sm: 12, md: 12},
        },
        {
          key: 'illness-conditions-coronary-artery-disease',
          element: (
            <QuoteFormElement
              elementType="Text"
              disabled
              label="Coronary Artery Bypass Surgery (25%)"
              adornment="percentage"
              type="number"
              value={getQuoteFieldValue<number>(initialPath.coronaryArteryDiseasePct)}
              id={initialPath.coronaryArteryDiseasePct}
              onChange={e => updateQuoteFieldValue(initialPath.coronaryArteryDiseasePct, Number(e.target.value) / 100)}
              onBlur={() => {
                handleValidationOnBlur();
              }}
              error={isDefined(
                ConditionPercentageError('coronaryArteryDiseasePct', 200, initialPath.coronaryArteryDiseasePct)
              )}
              helperText={ConditionPercentageError(
                'coronaryArteryDiseasePct',
                200,
                initialPath.coronaryArteryDiseasePct
              )}
              required
            />
          ),
          props: {xs: 12, sm: 12, md: 12},
        },
      ],
    },
    {
      rowProps: {key: 'illness-conditions-row-2'},
      items: [
        {
          key: 'cardio-conditions-cardiopulmonary-enhancement',
          element: (
            <QuoteFormControlElement
              controlType="checkbox"
              label="Cardiopulmonary Enhancement"
              checked={getQuoteFieldValue<boolean>(initialPath.isCardiopulmonaryEnhancement)}
              id={initialPath.isCardiopulmonaryEnhancement}
              disabled
            />
          ),
        },
        {
          key: 'cardio-conditions-cardiac-arrest',
          element: (
            <QuoteFormElement
              elementType="Text"
              disabled
              label="Sudden Cardiac Arrest (25%)"
              adornment="percentage"
              type="number"
              value={getQuoteFieldValue<number>(initialPath.suddenCardiacArrestPct)}
              id={initialPath.suddenCardiacArrestPct}
              onChange={e => updateQuoteFieldValue(initialPath.suddenCardiacArrestPct, Number(e.target.value) / 100)}
              onBlur={() => {
                handleValidationOnBlur();
              }}
              error={isDefined(
                ConditionPercentageError('suddenCardiacArrestPct', 200, initialPath.suddenCardiacArrestPct)
              )}
              helperText={ConditionPercentageError('suddenCardiacArrestPct', 200, initialPath.suddenCardiacArrestPct)}
            />
          ),
          props: {xs: 12, sm: 12, md: 12},
        },
        {
          key: 'cardio-conditions-fibrosis',
          element: (
            <QuoteFormElement
              elementType="Text"
              disabled
              label="Pulmonary Fibrosis (25%)"
              adornment="percentage"
              type="number"
              value={getQuoteFieldValue<number>(initialPath.idiopathicPulmonaryFibrosisPct)}
              id={initialPath.idiopathicPulmonaryFibrosisPct}
              onChange={e =>
                updateQuoteFieldValue(initialPath.idiopathicPulmonaryFibrosisPct, Number(e.target.value) / 100)
              }
              onBlur={() => {
                handleValidationOnBlur();
              }}
              error={isDefined(
                ConditionPercentageError(
                  'idiopathicPulmonaryFibrosisPct',
                  200,
                  initialPath.idiopathicPulmonaryFibrosisPct
                )
              )}
              helperText={ConditionPercentageError(
                'idiopathicPulmonaryFibrosisPct',
                200,
                initialPath.idiopathicPulmonaryFibrosisPct
              )}
            />
          ),
          props: {xs: 12, sm: 12, md: 12},
        },
        {
          key: 'cardio-conditions-pulmonary-embolism',
          element: (
            <QuoteFormElement
              elementType="Text"
              disabled
              label="Pulmonary Embolism (25%)"
              adornment="percentage"
              type="number"
              value={getQuoteFieldValue<number>(initialPath.pulmonaryEmbolismPct)}
              id={initialPath.pulmonaryEmbolismPct}
              onChange={e => updateQuoteFieldValue(initialPath.pulmonaryEmbolismPct, Number(e.target.value) / 100)}
              onBlur={() => {
                handleValidationOnBlur();
              }}
              error={isDefined(ConditionPercentageError('pulmonaryEmbolismPct', 200, initialPath.pulmonaryEmbolismPct))}
              helperText={ConditionPercentageError('pulmonaryEmbolismPct', 200, initialPath.pulmonaryEmbolismPct)}
            />
          ),
          props: {xs: 12, sm: 12, md: 12},
        },
      ],
    },
    {
      rowProps: {key: 'illness-conditions-row-3'},
      items: [
        {
          key: 'cardio-conditions-cardiopulmonary-enhancement',
          element: (
            <QuoteFormControlElement
              controlType="checkbox"
              label="Supplemental Critical Illness"
              checked={getQuoteFieldValue<boolean>(initialPath.isSupplementalCriticalIllnessApplied)}
              id={initialPath.isSupplementalCriticalIllnessApplied}
              disabled
            />
          ),
        },
        {
          key: 'supplemental-conditions-alzheimer',
          element: (
            <QuoteFormElement
              elementType="Text"
              disabled
              label="Advanced Alzheimer’s (100%)"
              adornment="percentage"
              type="number"
              value={getQuoteFieldValue<number>(initialPath.advancedAlzheimersPct)}
              id={initialPath.advancedAlzheimersPct}
              onChange={e => updateQuoteFieldValue(initialPath.advancedAlzheimersPct, Number(e.target.value) / 100)}
              onBlur={() => {
                handleValidationOnBlur();
              }}
              error={isDefined(
                ConditionPercentageError('advancedAlzheimersPct', 200, initialPath.advancedAlzheimersPct)
              )}
              helperText={ConditionPercentageError('advancedAlzheimersPct', 200, initialPath.advancedAlzheimersPct)}
            />
          ),
          props: {xs: 12, sm: 12, md: 12},
        },
        {
          key: 'supplemental-conditions-parkinson',
          element: (
            <QuoteFormElement
              elementType="Text"
              disabled
              label="Advanced Parkinson’s (100%)"
              adornment="percentage"
              type="number"
              value={getQuoteFieldValue<number>(initialPath.advancedParkinsonsPct)}
              id={initialPath.advancedParkinsonsPct}
              onChange={e => updateQuoteFieldValue(initialPath.advancedParkinsonsPct, Number(e.target.value) / 100)}
              onBlur={() => {
                handleValidationOnBlur();
              }}
              error={isDefined(
                ConditionPercentageError('advancedParkinsonsPct', 200, initialPath.advancedParkinsonsPct)
              )}
              helperText={ConditionPercentageError('advancedParkinsonsPct', 200, initialPath.advancedParkinsonsPct)}
            />
          ),
          props: {xs: 12, sm: 12, md: 12},
        },
        {
          key: 'supplemental-conditions-brain-tumor',
          element: (
            <QuoteFormElement
              elementType="Text"
              disabled
              label="Benign Brain Tumor (100%)"
              adornment="percentage"
              type="number"
              value={getQuoteFieldValue<number>(initialPath.benignBrainTumorPct)}
              id={initialPath.benignBrainTumorPct}
              onChange={e => updateQuoteFieldValue(initialPath.benignBrainTumorPct, Number(e.target.value) / 100)}
              onBlur={() => {
                handleValidationOnBlur();
              }}
              error={isDefined(ConditionPercentageError('benignBrainTumorPct', 200, initialPath.benignBrainTumorPct))}
              helperText={ConditionPercentageError('benignBrainTumorPct', 200, initialPath.benignBrainTumorPct)}
            />
          ),
          props: {xs: 12, sm: 12, md: 12},
        },
        {
          key: 'supplemental-conditions-coma',
          element: (
            <QuoteFormElement
              elementType="Text"
              disabled
              label="Coma (100%)"
              adornment="percentage"
              type="number"
              value={getQuoteFieldValue<number>(initialPath.comaPct)}
              id={initialPath.comaPct}
              onChange={e => updateQuoteFieldValue(initialPath.comaPct, Number(e.target.value) / 100)}
              onBlur={() => {
                handleValidationOnBlur();
              }}
              error={isDefined(ConditionPercentageError('comaPct', 200, initialPath.comaPct))}
              helperText={ConditionPercentageError('comaPct', 200, initialPath.comaPct)}
            />
          ),
          props: {xs: 12, sm: 12, md: 12},
        },
        {
          key: 'supplemental-conditions-loss-of-hearing',
          element: (
            <QuoteFormElement
              elementType="Text"
              disabled
              label="Complete Loss of Hearing (100%)"
              adornment="percentage"
              type="number"
              value={getQuoteFieldValue<number>(initialPath.lossOfHearingPct)}
              id={initialPath.lossOfHearingPct}
              onChange={e => updateQuoteFieldValue(initialPath.lossOfHearingPct, Number(e.target.value) / 100)}
              onBlur={() => {
                handleValidationOnBlur();
              }}
              error={isDefined(ConditionPercentageError('lossOfHearingPct', 200, initialPath.lossOfHearingPct))}
              helperText={ConditionPercentageError('lossOfHearingPct', 200, initialPath.lossOfHearingPct)}
            />
          ),
          props: {xs: 12, sm: 12, md: 12},
        },
        {
          key: 'supplemental-conditions-loss-of-sight',
          element: (
            <QuoteFormElement
              elementType="Text"
              disabled
              label="Complete Loss of Sight (100%)"
              adornment="percentage"
              type="number"
              value={getQuoteFieldValue<number>(initialPath.lossOfSightPct)}
              id={initialPath.lossOfSightPct}
              onChange={e => updateQuoteFieldValue(initialPath.lossOfSightPct, Number(e.target.value) / 100)}
              onBlur={() => {
                handleValidationOnBlur();
              }}
              error={isDefined(ConditionPercentageError('lossOfSightPct', 200, initialPath.lossOfSightPct))}
              helperText={ConditionPercentageError('lossOfSightPct', 200, initialPath.lossOfSightPct)}
            />
          ),
          props: {xs: 12, sm: 12, md: 12},
        },
        {
          key: 'supplemental-conditions-loss-of-speech',
          element: (
            <QuoteFormElement
              elementType="Text"
              disabled
              label="Complete Loss of Speech (100%)"
              adornment="percentage"
              type="number"
              value={getQuoteFieldValue<number>(initialPath.lossOfSpeechPct)}
              id={initialPath.lossOfSpeechPct}
              onChange={e => updateQuoteFieldValue(initialPath.lossOfSpeechPct, Number(e.target.value) / 100)}
              onBlur={() => {
                handleValidationOnBlur();
              }}
              error={isDefined(ConditionPercentageError('lossOfSpeechPct', 200, initialPath.lossOfSpeechPct))}
              helperText={ConditionPercentageError('lossOfSpeechPct', 200, initialPath.lossOfSpeechPct)}
            />
          ),
          props: {xs: 12, sm: 12, md: 12},
        },
        {
          key: 'supplemental-conditions-paralysis',
          element: (
            <QuoteFormElement
              elementType="Text"
              disabled
              label="Paralysis (100%)"
              adornment="percentage"
              type="number"
              value={getQuoteFieldValue<number>(initialPath.paralysisPct)}
              id={initialPath.paralysisPct}
              onChange={e => updateQuoteFieldValue(initialPath.paralysisPct, Number(e.target.value) / 100)}
              onBlur={() => {
                handleValidationOnBlur();
              }}
              error={isDefined(ConditionPercentageError('paralysisPct', 200, initialPath.paralysisPct))}
              helperText={ConditionPercentageError('paralysisPct', 200, initialPath.paralysisPct)}
            />
          ),
          props: {xs: 12, sm: 12, md: 12},
        },
        {
          key: 'supplemental-conditions-hiv',
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Occupational HIV (100%)"
              adornment="percentage"
              type="number"
              value={getQuoteFieldValue<number>(initialPath.occupationalHIVPct)}
              id={initialPath.occupationalHIVPct}
              onChange={e => updateQuoteFieldValue(initialPath.occupationalHIVPct, Number(e.target.value) / 100)}
              onBlur={() => {
                handleValidationOnBlur();
              }}
              error={isDefined(ConditionPercentageError('occupationalHIVPct', 200, initialPath.occupationalHIVPct))}
              helperText={ConditionPercentageError('occupationalHIVPct', 200, initialPath.occupationalHIVPct)}
              disabled
            />
          ),
          props: {xs: 12, sm: 12, md: 12},
        },
      ],
    },
  ];
  return <FormRowGrid rows={initialDiagnosis} />;
};

const RecurrenceDiagnosisBlock = () => {
  const {getQuoteFieldValue, updateQuoteFieldValue} = useQuoteField();
  const {selectedPlan} = useSelector((state: ReducerState) => state.quoteForm);
  const handleValidationOnBlur = useQuoteFormValidation();

  const recurrencePath = getBenefitArrayPaths(selectedPlan?.planIndex, coverageIndex, benefitName, 1);

  const initialPath = getBenefitArrayPaths(selectedPlan?.planIndex, coverageIndex, benefitName, 0);

  const ConditionPercentageError = (attributeName: string, amount: number, path: string) =>
    getFieldError(
      `CICoveredConditions.${attributeName}`,
      getQuoteFieldValue<number>(path) * 100,
      getValidationRuleset(A360ProductCd.CI, {attributeName, amount})
    );

  const recurrenceDiagnosis: GridRowProps[] = [
    {
      rowProps: {key: 'recurrence-illness-conditions-row-1'},
      title: 'Critical Illness Conditions',
      items: [
        {
          key: 'recurrence-illness-conditions-heart-attack',
          element: (
            <QuoteFormElement
              elementType="Text"
              disabled
              label="Heart Attack"
              adornment="percentage"
              type="number"
              value={getQuoteFieldValue<number>(recurrencePath.heartAttackPct)}
              id={recurrencePath.heartAttackPct}
              onChange={e => updateQuoteFieldValue(recurrencePath.heartAttackPct, Number(e.target.value) / 100)}
              onBlur={() => {
                handleValidationOnBlur();
              }}
              error={isDefined(
                ConditionPercentageError(
                  'heartAttackPct',
                  getQuoteFieldValue<number>(initialPath.heartAttackPct) * 100,
                  recurrencePath.heartAttackPct
                )
              )}
              helperText={ConditionPercentageError(
                'heartAttackPct',
                getQuoteFieldValue<number>(initialPath.heartAttackPct) * 100,
                recurrencePath.heartAttackPct
              )}
            />
          ),
          props: {xs: 12, sm: 12, md: 12},
        },
        {
          key: 'recurrence-illness-conditions-stroke',
          element: (
            <QuoteFormElement
              elementType="Text"
              disabled
              label="Stroke"
              adornment="percentage"
              type="number"
              value={getQuoteFieldValue<number>(recurrencePath.strokePct)}
              id={recurrencePath.strokePct}
              onChange={e => updateQuoteFieldValue(recurrencePath.strokePct, Number(e.target.value) / 100)}
              onBlur={() => {
                handleValidationOnBlur();
              }}
              error={isDefined(
                ConditionPercentageError(
                  'strokePct',
                  getQuoteFieldValue<number>(initialPath.strokePct) * 100,
                  recurrencePath.strokePct
                )
              )}
              helperText={ConditionPercentageError(
                'strokePct',
                getQuoteFieldValue<number>(initialPath.strokePct) * 100,
                recurrencePath.strokePct
              )}
            />
          ),
          props: {xs: 12, sm: 12, md: 12},
        },
        {
          key: 'recurrence-illness-conditions-renal-failure',
          element: (
            <QuoteFormElement
              elementType="Text"
              disabled
              label="End Stage Renal Failure"
              adornment="percentage"
              type="number"
              value={getQuoteFieldValue<number>(recurrencePath.kidneyRenalFailurePct)}
              id={recurrencePath.kidneyRenalFailurePct}
              onChange={e => updateQuoteFieldValue(recurrencePath.kidneyRenalFailurePct, Number(e.target.value) / 100)}
              onBlur={() => {
                handleValidationOnBlur();
              }}
              error={isDefined(
                ConditionPercentageError(
                  'kidneyRenalFailurePct',
                  getQuoteFieldValue<number>(initialPath.kidneyRenalFailurePct) * 100,
                  recurrencePath.kidneyRenalFailurePct
                )
              )}
              helperText={ConditionPercentageError(
                'kidneyRenalFailurePct',
                getQuoteFieldValue<number>(initialPath.kidneyRenalFailurePct) * 100,
                recurrencePath.kidneyRenalFailurePct
              )}
            />
          ),
          props: {xs: 12, sm: 12, md: 12},
        },
        {
          key: 'recurrence-illness-conditions-organ-transplant',
          element: (
            <QuoteFormElement
              elementType="Text"
              disabled
              label="Major Organ Transplant"
              adornment="percentage"
              type="number"
              value={getQuoteFieldValue<number>(recurrencePath.majorOrganFailurePct)}
              id={recurrencePath.majorOrganFailurePct}
              onChange={e => updateQuoteFieldValue(recurrencePath.majorOrganFailurePct, Number(e.target.value) / 100)}
              onBlur={() => {
                handleValidationOnBlur();
              }}
              error={isDefined(
                ConditionPercentageError(
                  'majorOrganFailurePct',
                  getQuoteFieldValue<number>(initialPath.majorOrganFailurePct) * 100,
                  recurrencePath.majorOrganFailurePct
                )
              )}
              helperText={ConditionPercentageError(
                'majorOrganFailurePct',
                getQuoteFieldValue<number>(initialPath.majorOrganFailurePct) * 100,
                recurrencePath.majorOrganFailurePct
              )}
            />
          ),
          props: {xs: 12, sm: 12, md: 12},
        },
        {
          key: 'recurrence-illness-conditions-coronary-artery-disease',
          element: (
            <QuoteFormElement
              elementType="Text"
              disabled
              label="Coronary Artery Bypass Surgery"
              adornment="percentage"
              type="number"
              value={getQuoteFieldValue<number>(recurrencePath.coronaryArteryDiseasePct)}
              id={recurrencePath.coronaryArteryDiseasePct}
              onChange={e =>
                updateQuoteFieldValue(recurrencePath.coronaryArteryDiseasePct, Number(e.target.value) / 100)
              }
              onBlur={() => {
                handleValidationOnBlur();
              }}
              error={isDefined(
                ConditionPercentageError(
                  'coronaryArteryDiseasePct',
                  getQuoteFieldValue<number>(initialPath.coronaryArteryDiseasePct) * 100,
                  recurrencePath.coronaryArteryDiseasePct
                )
              )}
              helperText={ConditionPercentageError(
                'coronaryArteryDiseasePct',
                getQuoteFieldValue<number>(initialPath.coronaryArteryDiseasePct) * 100,
                recurrencePath.coronaryArteryDiseasePct
              )}
            />
          ),
          props: {xs: 12, sm: 12, md: 12},
        },
      ],
    },
  ];
  return <FormRowGrid rows={recurrenceDiagnosis} />;
};

export const CriticalIllnessConditionsGVCI4Block = () => {
  const classes = usePortabilityStyles();
  const rows: GridRowProps[] = [
    {
      rowProps: {key: 'critical-illness-conditions-row-1', className: classes.columnAlignment},

      items: [
        {
          key: 'critical-illness-conditions-column-1',
          element: (
            <Grid container alignItems="center">
              <Grid spacing={1} style={{padding: '0 2rem'}}>
                <h4>Initial Diagnosis (% of Plan Benefit)</h4>
              </Grid>
              <Grid spacing={1} style={{padding: '0 1rem'}}>
                <InitialDiagnosisBlock />
              </Grid>
            </Grid>
          ),
          props: {xs: 6, sm: 6, md: 6},
        },
        {
          key: 'critical-illness-conditions-column-2',
          element: (
            <Grid container alignItems="center">
              <Grid spacing={1} style={{padding: '0 2rem'}}>
                <h4>Recurrence (% of Plan Benefit)</h4>
              </Grid>
              <Grid spacing={1} style={{padding: '0 2rem'}}>
                <RecurrenceDiagnosisBlock />
              </Grid>
            </Grid>
          ),
          props: {xs: 6, sm: 6, md: 6},
        },
      ],
    },
  ];

  return (
    <FormWrapper title="Critical Illness Conditions" collapsible withSeparator>
      <Grid container alignItems="center">
        <FormRowGrid rows={rows} />
      </Grid>
    </FormWrapper>
  );
};
